import { Link } from "react-router-dom";
import { Instagram, Twitter, Facebook, Youtube, Mail } from "lucide-react";
import Logo from "./Logo";
import { useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../contexts/SettingsContext";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const { settings } = useSettings();

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/newsletter", { email, source: "footer" });
            toast.success("You're on the list. Long live the Kings.");
            setEmail("");
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-bg-deep border-t border-line mt-24">
            <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-4 gap-10">
                <div className="md:col-span-2">
                    <Logo />
                    <p className="text-ink mt-5 max-w-md leading-relaxed">
                        Purple Kings · ABA Professional Basketball · Texas, since 2004. Built on grit, crowned in gold.
                    </p>
                    <form onSubmit={subscribe} className="mt-7 flex max-w-md" data-testid="footer-newsletter-form">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email for Royal Dispatch"
                            data-testid="footer-newsletter-email"
                            className="flex-1 px-4 py-3 text-sm"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="footer-newsletter-submit"
                            className="btn-gold px-5 py-3 text-xs disabled:opacity-60"
                        >
                            {loading ? "..." : "Join"}
                        </button>
                    </form>
                </div>

                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-gold font-bold mb-5">Visit</div>
                    <ul className="space-y-3 text-sm text-white">
                        <li><Link to="/about" className="hover:text-gold">About</Link></li>
                        <li><Link to="/roster" className="hover:text-gold">Roster</Link></li>
                        <li><Link to="/schedule" className="hover:text-gold">Upcoming Games</Link></li>
                        <li><Link to="/news" className="hover:text-gold">News</Link></li>
                        <li><Link to="/tryouts" className="hover:text-gold">Tryouts</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-gold font-bold mb-5">Connect</div>
                    <div className="flex gap-3 mb-5">
                        {settings?.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="p-2 border border-line hover:border-gold hover:text-gold" data-testid="social-ig"><Instagram size={16} /></a>}
                        {settings?.twitter_url && <a href={settings.twitter_url} target="_blank" rel="noreferrer" className="p-2 border border-line hover:border-gold hover:text-gold" data-testid="social-tw"><Twitter size={16} /></a>}
                        {settings?.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="p-2 border border-line hover:border-gold hover:text-gold" data-testid="social-fb"><Facebook size={16} /></a>}
                        {settings?.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="p-2 border border-line hover:border-gold hover:text-gold" data-testid="social-yt"><Youtube size={16} /></a>}
                        {!settings?.instagram_url && !settings?.twitter_url && !settings?.facebook_url && !settings?.youtube_url && (
                            <span className="text-ink text-xs">Social links coming soon</span>
                        )}
                    </div>
                    <a href={`mailto:${settings?.contact_email || "info@texaspurplekings.com"}`} className="text-sm text-ink hover:text-gold flex items-center gap-2">
                        <Mail size={14} /> {settings?.contact_email || "info@texaspurplekings.com"}
                    </a>
                </div>
            </div>
            <div className="border-t border-line py-6 text-center text-xs text-ink tracking-widest uppercase">
                © {new Date().getFullYear()} Purple Kings. All Rights Reserved.
            </div>
        </footer>
    );
}
